<template>
  <v-dialog v-model="showDialog" persistent>
    <v-card outlined class="pa-3" v-if="loaded">
      <v-col class="text-right px-4">
        <v-btn icon @click="closeDialog">
          <v-icon>close</v-icon>
        </v-btn>
      </v-col>

      <v-card-title>
        <h6 class="mb-0 font-weight-bold">{{ mergePreview.customer.name}}</h6>
      </v-card-title>
      <v-card-text>
        <v-row>
          <!-- Left side -->
          <v-col>
            <LabelField
                :label="$t('settings/settings:duplicate.preview.customer.reg-number')"
                :value="mergePreview.customer.regNumber ? mergePreview.customer.regNumber : '-'"
                flex
            />

            <h6 class="mt-3 mb-0 font-weight-bold">{{ $t('common:address') }}</h6>
            <p class="mb-0">{{ mergePreview.customer.street }}</p>
            <p class="mb-0">{{ mergePreview.customer.zip }} {{ mergePreview.customer.city }}</p>
            <p>{{ mergePreview.customer.provinceName }} - {{ mergePreview.customer.countryName }}</p>

            <h6 class="mt-3 mb-0 font-weight-bold">{{ $t('settings/settings:duplicate.preview.customer.invoices') }}</h6>
            <LabelField
                label="BIC"
                :value="mergePreview.customer.bic ? mergePreview.customer.bic : '-'"
                flex
            />

            <LabelField
                label="IBAN"
                :value="mergePreview.customer.iban ? mergePreview.customer.iban : '-'"
                flex
            />

            <LabelField
                :label="$t('settings/settings:duplicate.preview.customer.vat')"
                :value="mergePreview.customer.vatId ? mergePreview.customer.vatId : '-'"
                flex
            />

            <h6 class="mt-3 mb-0 font-weight-bold">{{ $t('settings/settings:duplicate.preview.customer.contact-data') }}</h6>
            <LabelField
                :label="$t('common:name')"
                :value="mergePreview.customer.contactName ? mergePreview.customer.contactName : '-'"
                flex
            />

            <LabelField
                :label="$t('common:email')"
                :value="mergePreview.customer.contactMail ? mergePreview.customer.contactMail : '-'"
                flex
            />

            <LabelField
                :label="$t('common:phone')"
                :value="mergePreview.customer.contactPhone ? mergePreview.customer.contactPhone : '-'"
                flex
            />
          </v-col>
          <!-- Right side -->
          <v-col>
            <h6 class="mt-3 mb-0 font-weight-bold">{{ $t('settings/settings:duplicate.preview.customer.other') }}</h6>
            <LabelField
                :label="$t('settings/settings:duplicate.preview.customer.parent-customer')"
                :value="mergePreview.customer.parentName ? mergePreview.customer.parentName : '-'"
                flex
            />

            <v-row>
              <v-col>
                <p class="mb-0 font-weight-bold">{{ $t('settings/settings:duplicate.preview.customer.tags') }}</p>
                <v-list>
                  <v-list-item v-for="(tagName, index) in mergePreview.customer.tagNames" :key="index" dense class="py-0">
                    <v-list-item-icon class="mr-0">
                      <v-icon x-small>radio_button_unchecked</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content class="ml-2">
                      <v-list-item-title>{{ tagName }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-col>

              <v-col>
                <p class="mb-0 font-weight-bold">{{ $t('settings/settings:duplicate.preview.customer.black-tags') }}</p>
                <v-list>
                  <v-list-item v-for="(tagName, index) in mergePreview.customer.blackTagNames" :key="index" dense class="py-0">
                    <v-list-item-icon class="mr-0">
                      <v-icon x-small>radio_button_unchecked</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content class="ml-2">
                      <v-list-item-title>{{ tagName }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-col>
            </v-row>

            <LabelField
                :label="$t('settings/settings:duplicate.preview.customer.commission')"
                :value="mergePreview.customer.provision ? mergePreview.customer.provision : '-'"
                flex
            />

            <div class="d-flex">
              <v-checkbox
                  :label="$t('settings/settings:duplicate.preview.customer.advertiser')"
                  v-model="mergePreview.customer.advertiser"
                  disabled
                  hide-details
                  class="mr-3"
              />

              <v-checkbox
                  :label="$t('settings/settings:duplicate.preview.customer.inhouse')"
                  v-model="mergePreview.customer.inhouse"
                  disabled
                  hide-details
                  class="mr-3"
              />

              <v-checkbox
                  :label="$t('settings/settings:duplicate.preview.customer.facility-operator')"
                  v-model="mergePreview.customer.facilityOperator"
                  disabled
                  hide-details
              />
            </div>
          </v-col>
        </v-row>

        <h6 class="mb-0 font-weight-bold">{{ $t('common:user_plural') }}</h6>
        <DataTable
            :headers="usersHeader"
            :data="mergePreview.users"
            icon="visibility"
            :iconTooltip="$t('buttons:show')"
            @clickedButton1="viewUser"
            checkbox-on-property="active"
            sortBy="name"
        />

        <h6 class="mb-0 font-weight-bold">{{ $t('settings/settings:duplicate.preview.customer.child-customers') }}</h6>
        <DataTable
            :headers="childrenHeader"
            :data="mergePreview.children"
            icon="visibility"
            :iconTooltip="$t('buttons:show')"
            @clickedButton1="viewCustomer"
            sortBy="name"
        />

        <h6 class="mb-0 font-weight-bold">{{ $t('settings/settings:duplicate.preview.customer.owner-buildings') }}</h6>
        <DataTable
            :headers="buildingHeader"
            :data="mergePreview.ownerBuildings"
            icon="visibility"
            :iconTooltip="$t('buttons:show')"
            @clickedButton1="viewBuilding"
            sortBy="name"
        />

        <h6 class="mb-0 font-weight-bold">{{ $t('settings/settings:duplicate.preview.customer.operator-buildings') }}</h6>
        <DataTable
            :headers="buildingHeader"
            :data="mergePreview.operatorBuildings"
            icon="visibility"
            :iconTooltip="$t('buttons:show')"
            @clickedButton1="viewBuilding"
            sortBy="name"
        />

        <h6 class="mb-0 font-weight-bold">{{ $t('settings/settings:duplicate.preview.customer.facilities') }}</h6>
        <DataTable
            :headers="facilityHeader"
            :data="mergePreview.facilities"
            icon="visibility"
            :iconTooltip="$t('buttons:show')"
            @clickedButton1="viewFacility"
            sortBy="name"
        />

        <h6 class="mb-0 font-weight-bold">{{ $t('settings/settings:duplicate.preview.customer.contracts') }}</h6>
        <DataTable
            :headers="contractHeader"
            :data="mergePreview.contracts"
            icon="visibility"
            :iconTooltip="$t('buttons:show')"
            @clickedButton1="viewContract"
            sortBy="name"
        />

        <v-row>
          <v-col>
            <h6 class="mb-0 font-weight-bold">{{ $t('settings/settings:duplicate.preview.customer.tags') }}</h6>
            <DataTable
                :headers="tagHeader"
                :data="mergePreview.tags"
                sortBy="name"
            />
          </v-col>

          <v-col>
            <h6 class="mb-0 font-weight-bold">{{ $t('settings/settings:duplicate.preview.customer.black-tags') }}</h6>
            <DataTable
                :headers="tagHeader"
                :data="mergePreview.blackTags"
                sort-by="name"
            />
          </v-col>
        </v-row>

        <h6 class="mb-0 font-weight-bold">{{ $t('settings/settings:duplicate.preview.customer.options') }}</h6>
        <DataTable
            :headers="optionHeader"
            :data="mergePreview.options"
            sort-by="option.name"
        />

        <h6 class="mb-0 font-weight-bold">{{ $t('common:media_plural') }}</h6>
        <DataTable
            :headers="advertHeader"
            :data="mergePreview.adverts"
            sort-by="name"
            icon="visibility"
            :iconTooltip="$t('buttons:show')"
            @clickedButton1="viewAdvert"
            checkbox-on-property="active"
        />

        <h6 class="mb-0 font-weight-bold">{{ $t('settings/settings:duplicate.preview.customer.templates') }}</h6>
        <DataTable
            :headers="templateHeader"
            :data="mergePreview.templates"
            sort-by="name"
        />

        <h6 class="mb-0 font-weight-bold">{{ $t('common:elevator_plural') }}</h6>
        <DataTable
            :headers="elevatorHeader"
            :data="mergePreview.elevators"
            sort-by="name"
            checkbox-on-property="active"
            icon="visibility"
            :iconTooltip="$t('buttons:show')"
            @clickedButton1="viewElevator"
        />

        <h6 class="mb-0 font-weight-bold">{{ $t('settings/settings:duplicate.preview.customer.offers') }}</h6>
        <DataTable
            :headers="offerHeader"
            :data="mergePreview.offers"
            sort-by="name"
            icon="visibility"
            :iconTooltip="$t('buttons:show')"
            @clickedButton1="viewOffer"
        />

        <h6 class="mb-0 font-weight-bold">{{ $t('settings/settings:duplicate.preview.customer.pdf') }}</h6>
        <DataTable
            :headers="pdfHeader"
            :data="mergePreview.pdfs"
            sort-by="name"
        />

        <v-card-actions>
          <BaseButton
              :btn-text="$t('buttons:cancel')"
              color="grey"
              @clickedButton="closeDialog()"
          />

          <BaseButton
              :btn-text="$t('settings/settings:duplicate.preview.confirm-merge')"
              color="warning"
              @clickedButton="$refs.warningDialog.openDialog()"
          />
        </v-card-actions>
      </v-card-text>
    </v-card>

    <MergeWarningDialog
        ref="warningDialog"
        :objects-to-remove-names="secondaryCustomerNames"
        :object-translation="$t('common:customer_plural')"
        @confirmMerge="confirmMerge"
    />
  </v-dialog>
</template>

<script>
import DuplicateCheckService from "@/modules/core/services/duplicate-check-service";
import CountryService from "@/modules/core/services/country-service";
import Utils from "@/modules/core/services/utils";
import LabelField from "@/modules/core/components/LabelField";
import CustomerService from "@/modules/core/services/customer-service";
import TagService from "@/modules/settings/services/tag-service";
import DataTable from "@/modules/core/components/DataTable/DataTable";
import BaseButton from "@/modules/core/components/Base/BaseButton";
import MergeWarningDialog from "@/modules/settings/views/settings/duplicateMerge/MergeWarningDialog";

export default {
  name: "CustomerMerging",
  components: {MergeWarningDialog, BaseButton, DataTable, LabelField},
  data() {
    return {
      mergePreview: {},
      mainCustomerCountry: null,
      mainCustomerProvince: null,
      loaded: false,
      mainCustomerId: null,
      secondaryCustomerIds: [],
      secondaryCustomerNames: [],
      showDialog: false
    }
  },
  computed: {
    usersHeader() {
      return [
        {text: this.$t('common:username'), value: 'username'},
        {text: this.$t('common:name'), value: 'name'},
        {text: this.$t('common:email'), value: 'email'},
        {text: this.$t('common:active'), value: 'checkbox'},
        {text: '', value: 'controls'}
      ]
    },
    childrenHeader() {
      return [
        {text: this.$t('common:name'), value: 'name'},
        {text: this.$t('common:street'), value: 'street'},
        {text: this.$t('common:zip'), value: 'zip'},
        {text: this.$t('common:city'), value: 'city'},
        {text: '', value: 'controls'}
      ]
    },
    buildingHeader() {
      return [
        {text: this.$t('common:name'), value: 'name'},
        {text: this.$t('common:object-number'), value: 'objectNumber'},
        {text: this.$t('common:street'), value: 'addressStreet'},
        {text: '', value: 'controls'}
      ]
    },

    facilityHeader() {
      return [
        {text: this.$t('common:name'), value: 'name'},
        {text: this.$t('common:facility-number'), value: 'objectNumber'},
        {text: '', value: 'controls'}
      ]
    },

    contractHeader() {
      return [
        {text: this.$t('common:name'), value: 'name'},
        {text: this.$t('settings/settings:duplicate.preview.customer.contract-types'), value: 'translatedTypeString'},
        {text: '', value: 'controls'}
      ]
    },

    tagHeader() {
      return [
        {text: this.$t('common:name'), value: 'name'}
      ]
    },

    optionHeader() {
      return [
        {text: this.$t('common:name'), value: 'option.name'},
        {text: 'Wert', value: 'value'}
      ]
    },

    advertHeader() {
      return [
        {text: this.$t('common:name'), value: 'name'},
        {text: this.$t('common:start-date'), value: 'dateStart'},
        {text: this.$t('common:end-date'), value: 'dateEnd'},
        {text: this.$t('common:active'), value: 'checkbox'},
        {text: '', value: 'controls'}
      ]
    },

    templateHeader() {
      return [
        {text: this.$t('common:name'), value: 'name'},
        {text: this.$t('common:start-date'), value: 'dateStart'},
        {text: this.$t('common:end-date'), value: 'dateEnd'},
        {text: '', value: 'controls'}
      ]
    },

    elevatorHeader() {
      return [
        {text: this.$t('common:name'), value: 'name'},
        {text: this.$t('common:active'), value: 'checkbox'},
        {text: this.$t('common:street'), value: 'address'},
        {text: this.$t('common:zip'), value: 'zip'},
        {text: this.$t('common:city'), value: 'city'},
        {text: '', value: 'controls'}
      ]
    },

    offerHeader() {
      return [
        {text: this.$t('common:name'), value: 'name'},
        {text: this.$t('common:start-date'), value: 'dateStart'},
        {text: this.$t('common:end-date'), value: 'dateEnd'},
        {text: '', value: 'controls'}
      ]
    },

    pdfHeader() {
      return [
        {text: this.$t('common:name'), value: 'name'},
        {text: this.$t('common:type'), value: 'type'},
        {text: this.$t('common:status'), value: 'status'},
        {text: this.$t('common:created_at'), value: 'createdAt'},
      ]
    }
  },
  methods: {
    closeDialog() {
      this.mergePreview = {};
      this.loaded = false;
      this.mainCustomerId = null;
      this.secondaryCustomerIds = [];
      this.secondaryCustomerNames = [];
      this.showDialog = false;
    },

    getAllContractTypes(item) {
      let typeStrings;
      if (item.translatedTypes.length > 1) {
        typeStrings = item.translatedTypes[0] + ", ";

        for (let i = 1; i < item.translatedTypes.length - 1; i++) {
          typeStrings += item.translatedTypes[i] + ", ";
        }

        typeStrings += item.translatedTypes[item.translatedTypes.length - 1];
      } else {
        typeStrings = item.translatedTypes[0];
      }

      return typeStrings;
    },

    createPreview(duplicateGroup) {
      this.showDialog = true;
      let customerIds = [];
      for (let customer of duplicateGroup.objects) {
        if (duplicateGroup.primaryObjectId !== customer.id) {
          customerIds.push(customer.id);
          this.secondaryCustomerIds.push(customer.id);
          this.secondaryCustomerNames.push(customer.name);
        }
      }

      this.mainCustomerId = duplicateGroup.primaryObjectId;

      DuplicateCheckService.readCustomerMergePreview(duplicateGroup.primaryObjectId, customerIds,
          (res) => {
            this.mergePreview = res.data;

            // if a parent customer is set, read it to get the name of it
            if (this.mergePreview.customer.parentId) {
              CustomerService.readFromAPI(this.mergePreview.customer.parentId,
                  (res) => {
                    this.mergePreview.customer.parentName = res.data.name;
                  }
              )
            }

            // if tags are set for the customer, fetch the names of the tags
            if (this.mergePreview.customer.tags && this.mergePreview.customer.tags.length > 0) {
              this.mergePreview.customer.tagNames = [];
              for (let tagId of this.mergePreview.customer.tags) {
                TagService.readFromAPI(tagId,
                    (resTag) => {
                      this.mergePreview.customer.tagNames.push(resTag.data.name);
                    }
                )
              }
            }

            // if black tags are set for the customer, fetch the names of the tags
            if (this.mergePreview.customer.blackTags && this.mergePreview.customer.blackTags.length > 0) {
              this.mergePreview.customer.blackTagNames = [];
              for (let tagId of this.mergePreview.customer.blackTags) {
                TagService.readFromAPI(tagId,
                    (resTag) => {
                      this.mergePreview.customer.blackTagNames.push(resTag.data.name);
                    }
                )
              }
            }

            // translate contract types
            this.mergePreview.contracts.forEach((contract) => {
              contract.translatedTypes = [];
              contract.types.forEach((type) => {
                contract.translatedTypes.push(this.$t('enums:contract-types.' + type))
              })
              contract.translatedTypeString = this.getAllContractTypes(contract);
              contract.warning = !contract.types || contract.types.length === 0;
            })

            CountryService.readFromAPI(this.mergePreview.customer.country,
                (res) => {
                  this.mainCustomerCountry = res.data;
                  this.mergePreview.customer.countryName = Utils.translateCountry(this.mainCustomerCountry.name);

                  CountryService.readProvincesOfCountry(this.mergePreview.customer.country,
                      (resProvinces) => {
                        resProvinces.data.forEach((province) => {
                          if (this.mergePreview.customer.province === province.id) {
                            this.mainCustomerProvince = province;
                            this.mergePreview.customer.provinceName = Utils.translateProvinces(this.mainCustomerCountry.name, province.name);
                          }
                        })

                        this.loaded = true;
                      },
                      (error) => {
                        this.$logger.error("Could not read provinces of country with id '" + this.mergePreview.customer.country + "', because: " + error);
                      }
                  )
                },
                (error) => {
                  this.$logger.error("Could not read country data with id '" + this.mergePreview.customer.country + "', because: " + error);
                }
            )
          },
          (error) => {
            this.$logger.error("Failed reading customer merge preview, because: " + error);
            Utils.showFailureSnackbar(this.$t('settings/settings:duplicate.preview.snackbar.failed-reading-preview'))
          }
      )
    },

    confirmMerge() {
      this.$refs.warningDialog.closeDialog();
      DuplicateCheckService.mergeCustomers(this.mainCustomerId, this.secondaryCustomerIds,
          () => {
            Utils.showSuccessSnackbar(this.$t('settings/settings:duplicate.preview.snackbar.merge-successful', { name: this.$t('common:customer_plural')}));
            this.$emit('finishedMerge', this.secondaryCustomerIds);
            this.closeDialog();
          },
          (error) => {
            Utils.showFailureSnackbar(this.$t('settings/settings:duplicate.preview.snackbar.merge-failed', { name: this.$t('common:customer_plural')}));
            this.$logger.error("Failed merging customers because: " + error);
          }
      )
    },

    viewUser(user) {
      window.open(`/${this.$store.state.manager.settingsModuleUrl}/profile/customer/users?user=${user.id}`);
    },

    viewCustomer(customer) {
      window.open(`/${this.$store.state.manager.settingsModuleUrl}/customer/edit/${customer.id}`)
    },

    viewBuilding(building) {
      window.open(`/${this.$store.state.manager.bookModuleUrl}/buildings/edit/${building.id}`);
    },

    viewFacility(facility) {
      window.open(`/${this.$store.state.manager.bookModuleUrl}/facilities/edit/${facility.id}`);
    },

    viewContract(contract) {
      window.open(`/${this.$store.state.manager.bookModuleUrl}/facilities/edit/${contract.facilityId}/info/contracts`);
    },

    viewAdvert(advert) {
      window.open(`/${this.$store.state.manager.displayModuleUrl}/advert?advert=${advert.id}`);
    },

    viewElevator(elevator) {
      window.open(`/${this.$store.state.manager.displayModuleUrl}/elevator?elevator=${elevator.id}`);
    },

    viewOffer(offer) {
      window.open(`/${this.$store.state.manager.displayModuleUrl}/offer?offer=${offer.id}`);
    }
  }
}
</script>

<style scoped>

</style>